import {
	Button,
	Id,
	Loading,
	ModalBody,
	ModalFooter,
	Overlay,
	SingleSelectField,
	SubmitButton,
	createReactComponentsPortal,
} from "@dgs/core";
import { Form, Formik, FormikErrors } from "formik";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useRegionOptionsQuery } from "../shared/api/regions/useRegionsQuery";
import { RegionOptionResource } from "../shared/types/region";

const ModalOverlay = styled(Overlay)`
	z-index: ${({ theme }) => theme.zIndices.portal};
	${({ theme }) => theme.modal.background};
`;

const ModalContainer = styled.div`
	${({ theme }) => theme.modal.container};
	max-width: 40rem;
	${({ theme }) => theme.modal.overflowContainer};
`;

const HeaderContainer = styled.div`
	${({ theme }) => theme.modal.header};
`;

const Heading = styled.h2`
	${({ theme }) => theme.modal.heading};
`;

const RegionSelectWrapper = styled.div`
	${({ theme }) => theme.card.body};
`;

const RegionButtonGroup = styled.div`
	display: grid;
	justify-content: center;
	gap: ${({ theme }) => theme.spacing(2)};
`;

interface FormValues {
	region: RegionOptionResource | null;
}

const initialValues: FormValues = {
	region: null,
};

export const RegionModal: FC<{ setRegionId: (regionId: Id) => void }> = ({ setRegionId }) => {
	const { t } = useTranslation();
	const { data: regions } = useRegionOptionsQuery();

	return createReactComponentsPortal(
		<ModalOverlay>
			<ModalContainer>
				<HeaderContainer>
					<Heading>{t("Select your region")}</Heading>
				</HeaderContainer>
				<ModalBody>{t("Please select a region so that we can display the appropriate information.")}</ModalBody>
				{!regions && <Loading />}
				{regions &&
					(regions.length > 4 ? (
						<RegionsSelect onSelect={setRegionId} regions={regions} />
					) : (
						<RegionButtons onSelect={setRegionId} regions={regions} />
					))}
			</ModalContainer>
		</ModalOverlay>,
		document.body,
	);
};

const RegionButtons: FC<{ onSelect: (regionId: Id) => void; regions: RegionOptionResource[] }> = ({
	onSelect,
	regions,
}) => {
	return (
		<RegionSelectWrapper>
			<RegionButtonGroup>
				{regions.map((region) => (
					<Button key={region.id} color="primary" onClick={() => onSelect(region.id)}>
						{region.name}
					</Button>
				))}
			</RegionButtonGroup>
		</RegionSelectWrapper>
	);
};

const RegionsSelect: FC<{ onSelect: (regionId: Id) => void; regions: RegionOptionResource[] }> = ({
	onSelect,
	regions,
}) => {
	const { t } = useTranslation();

	const validate = useCallback(
		(values: FormValues) => {
			const errors: FormikErrors<FormValues> = {};

			if (!values.region) {
				errors.region = t("Field is required");
			}

			return errors;
		},
		[t],
	);

	return (
		<Formik
			initialValues={initialValues}
			onSubmit={(values) => onSelect((values.region as RegionOptionResource).id)}
			validate={validate}
		>
			<Form>
				<RegionSelectWrapper>
					<SingleSelectField name="region" options={regions} label={t("Region")} />
				</RegionSelectWrapper>
				<ModalFooter>
					<SubmitButton>{t("Next")} </SubmitButton>
				</ModalFooter>
			</Form>
		</Formik>
	);
};
